import React, { useMemo, useCallback } from 'react'
import { matchPath, match as RouterMatch } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { accountingApiEvents, accountingApiSelectors } from '../../../api/accounting'
import { subscribe } from 'react-contextual'
import { path, pipe, props, join, pathOr } from 'ramda'
import { ROUTES } from 'constants/routes'
import ViewStatementProvider from '../ViewStatement/ViewStatementProvider'
import PortfoliosProvider from '../../../portfolios/PortfoliosProvider'
import ViewStatement from '../ViewStatement/ViewStatement'
import { $TSFixMe } from 'types/ts-migrate'
import { RootState } from 'src/state/types'

type StatementFormat = 'json' | 'pdf' | 'csv'

const STATEMENT_EVENTS = {
  json: accountingApiEvents.portfolioStatement_request,
  pdf: accountingApiEvents.portfolioStatementPdf_request,
  csv: accountingApiEvents.portfolioStatementCsv_request,
}

interface PortfolioStatementProps {
  match: RouterMatch
  redirect: (path: string) => void
}

const PortfolioStatement = ({ match, redirect }: PortfolioStatementProps): null | React.ReactElement => {
  const editMatchPath = matchPath(match.url, {
    path: ROUTES.portfolioStatement,
    exact: true,
    strict: false,
  })

  const portfolioId: string | undefined = path(['params', 'id'], editMatchPath)

  const dispatch = useDispatch()

  const fetchPortfolioStatement = useCallback(
    (startDate: string, endDate: string, format: StatementFormat = 'json') => {
      if (portfolioId) {
        dispatch(
          STATEMENT_EVENTS[format]({
            portfolioId,
            start_date: startDate,
            end_date: endDate,
          }),
        )
      }
    },
    [portfolioId, dispatch],
  )

  const fetchPortfolioStatementPdf = useCallback(
    (startDate: string, endDate: string) => fetchPortfolioStatement(startDate, endDate, 'pdf'),
    [fetchPortfolioStatement],
  )
  const fetchPortfolioStatementCsv = useCallback(
    (startDate: string, endDate: string) => fetchPortfolioStatement(startDate, endDate, 'csv'),
    [fetchPortfolioStatement],
  )
  const statementData = useSelector((state: RootState) =>
    accountingApiSelectors.getPortfolioStatementById(state)(portfolioId as string),
  )
  const statementPdf = useSelector((state: RootState) =>
    accountingApiSelectors.getPortfolioStatementPdfById(state)(portfolioId as string),
  )
  const statementCsv = useSelector((state: RootState) =>
    accountingApiSelectors.getPortfolioStatementCsvById(state)(portfolioId as string),
  )
  const fileName = pipe(
    props(['addressLine1', 'addressLine2']),
    join(', '),
    // @ts-expect-error
  )(pathOr({}, ['context', 'property'], statementData))
  const isOpen = portfolioId

  return useMemo(() => {
    return isOpen ? (
      <ViewStatementProvider
        onClose={() => redirect(`/leases/${portfolioId}`)}
        onParamChange={fetchPortfolioStatement}
        isOpen={isOpen}
        statementData={statementData}
        downloadStatement={fetchPortfolioStatementPdf}
        statementPdf={statementPdf}
        pdfFileName={fileName}
        downloadStatementCsv={fetchPortfolioStatementCsv}
        statementCsv={statementCsv}
        csvFileName={fileName}
      >
        <ViewStatement title="Lease Statement" />
      </ViewStatementProvider>
    ) : null
  }, [
    redirect,
    fetchPortfolioStatement,
    fetchPortfolioStatementPdf,
    fetchPortfolioStatementCsv,
    statementData,
    statementPdf,
    statementCsv,
    isOpen,
    fileName,
    portfolioId,
  ])
}

export default subscribe([PortfoliosProvider], ({ match, redirect }: $TSFixMe) => ({ match, redirect }))(
  PortfolioStatement,
)
